import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RocketLaunch, Bug, Lightbulb, ArrowSquareOut, CheckCircle, Copy, Hammer, ClockCounterClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_META = {
  planned: { label: "Planned", cls: "bg-slate-100 text-slate-600" },
  in_development: { label: "In Development", cls: "bg-blue-50 text-blue-600" },
  testing: { label: "Testing", cls: "bg-purple-50 text-purple-600" },
  coming_soon: { label: "Coming Soon", cls: "bg-amber-50 text-amber-700" },
  released: { label: "Released", cls: "bg-emerald-50 text-emerald-600" },
  on_hold: { label: "On Hold", cls: "bg-gray-100 text-gray-500" },
};

const REQ_STATUS_META = {
  submitted: { label: "Submitted", cls: "bg-slate-100 text-slate-600" },
  under_review: { label: "Under Review", cls: "bg-blue-50 text-blue-600" },
  accepted: { label: "Accepted", cls: "bg-teal-50 text-teal-600" },
  considering: { label: "Considering", cls: "bg-teal-50 text-teal-600" },
  planned: { label: "Planned", cls: "bg-indigo-50 text-indigo-600" },
  in_progress: { label: "In Progress", cls: "bg-blue-50 text-blue-700" },
  in_development: { label: "In Development", cls: "bg-blue-50 text-blue-700" },
  testing: { label: "Testing", cls: "bg-purple-50 text-purple-600" },
  resolved: { label: "Resolved", cls: "bg-emerald-50 text-emerald-600" },
  released: { label: "Released", cls: "bg-emerald-50 text-emerald-600" },
  closed: { label: "Closed", cls: "bg-gray-100 text-gray-500" },
  unable_to_reproduce: { label: "Unable to Reproduce", cls: "bg-amber-50 text-amber-700" },
  not_planned: { label: "Not Planned", cls: "bg-gray-100 text-gray-500" },
};

const FILTERS = [["all", "All"], ["planned", "Planned"], ["in_development", "In Development"], ["testing", "Testing"], ["coming_soon", "Coming Soon"], ["released", "Released"]];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Badge = ({ meta }) => (
  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${meta?.cls || "bg-gray-100 text-gray-500"}`}>{meta?.label || "—"}</span>
);

const CatChip = ({ cat }) => (
  <span className="text-[10px] font-bold text-[#8a90a0] border border-[#E5E7EB] rounded-full px-2 py-0.5">{cat}</span>
);

const deviceType = () => (window.matchMedia("(max-width: 768px)").matches ? "mobile" : "desktop");
const buildContext = () => ({ browser: navigator.userAgent, device: deviceType(), route: window.location.pathname });

const SuccessView = ({ reference, kind, onClose }) => (
  <div className="text-center py-6" data-testid="feedback-success-view">
    <span className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center">
      <CheckCircle size={28} weight="duotone" className="text-emerald-500" />
    </span>
    <h3 className="mt-4 font-heading text-lg font-extrabold text-[#0A0D14]">{kind} submitted successfully</h3>
    <p className="mt-1 text-sm text-[#525866]">Your reference number:</p>
    <button onClick={() => { navigator.clipboard?.writeText(reference); toast.success("Reference copied"); }}
      data-testid="feedback-success-ref"
      className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[#0A0D14] text-white font-mono font-bold text-base px-5 py-2.5 hover:bg-[#1c2230]">
      {reference} <Copy size={15} />
    </button>
    <p className="mt-3 text-xs text-[#8a90a0]">Track it anytime under My Requests on this page.</p>
    <Button onClick={onClose} data-testid="feedback-success-close" className="mt-5 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm h-10 px-6">Done</Button>
  </div>
);

const BugDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [title, setTitle] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (open) { setPageUrl(window.location.href); setRef(null); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", {
        type: "BUG", title, description: notes, page_url: pageUrl, attachment_link: link, context: buildContext(),
      });
      setRef(data.reference_number);
      setTitle(""); setNotes(""); setLink("");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="bug-dialog">
        {ref ? <SuccessView reference={ref} kind="Bug report" onClose={() => onOpenChange(false)} /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-extrabold flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg bg-red-50 flex items-center justify-center"><Bug size={17} weight="duotone" className="text-red-500" /></span>
                Report a Bug
              </DialogTitle>
              <DialogDescription className="text-xs">Found something that doesn't work as expected? Tell us and we'll look into it.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Title *</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Briefly describe the problem"
                  data-testid="bug-title-input" className="mt-1 h-10 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Page / Link</label>
                <Input value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} placeholder="Where did it happen?"
                  data-testid="bug-page-input" className="mt-1 h-10 rounded-lg font-mono text-xs" />
                <p className="mt-0.5 text-[10px] text-[#8a90a0]">Auto-captured — edit if the issue is on a different page.</p>
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Notes *</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} required rows={4}
                  placeholder="Tell us what happened, what you expected to happen, and any steps that can help us reproduce the issue."
                  data-testid="bug-notes-input"
                  className="mt-1 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm outline-none focus:border-[#FF6B35] resize-none" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Screenshot / attachment link</label>
                <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Paste a link to a screenshot or PDF (Drive, Dropbox…)"
                  data-testid="bug-attachment-input" className="mt-1 h-10 rounded-lg" />
              </div>
              <Button type="submit" disabled={busy} data-testid="bug-submit-btn"
                className="w-full rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm h-11">
                {busy ? "Submitting…" : "Submit Bug Report"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const FeatureDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [title, setTitle] = useState("");
  const [what, setWhat] = useState("");
  const [why, setWhy] = useState("");
  const [pageUrl, setPageUrl] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => { if (open) setRef(null); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/feedback", {
        type: "FEATURE_REQUEST", title, description: what, business_value: why,
        page_url: pageUrl, attachment_link: link, context: buildContext(),
      });
      setRef(data.reference_number);
      setTitle(""); setWhat(""); setWhy(""); setPageUrl(""); setLink("");
      onSubmitted?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="feature-dialog">
        {ref ? <SuccessView reference={ref} kind="Feature request" onClose={() => onOpenChange(false)} /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-extrabold flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center"><Lightbulb size={17} weight="duotone" className="text-amber-500" /></span>
                Suggest a Feature
              </DialogTitle>
              <DialogDescription className="text-xs">Tell us what would make managing and optimizing your ads easier.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Feature Title *</label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="e.g., Automatically pause high-CPL ads"
                  data-testid="feature-title-input" className="mt-1 h-10 rounded-lg" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">What would you like AdPilot to do? *</label>
                <textarea value={what} onChange={(e) => setWhat(e.target.value)} required rows={3}
                  placeholder="Describe the feature…" data-testid="feature-what-input"
                  className="mt-1 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm outline-none focus:border-[#FF6B35] resize-none" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#0A0D14]">Why would this be useful? *</label>
                <textarea value={why} onChange={(e) => setWhy(e.target.value)} required rows={2}
                  placeholder="Your use case — how would this help your business?" data-testid="feature-why-input"
                  className="mt-1 w-full rounded-lg border border-[#E5E7EB] p-3 text-sm outline-none focus:border-[#FF6B35] resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-[#0A0D14]">Relevant page / link</label>
                  <Input value={pageUrl} onChange={(e) => setPageUrl(e.target.value)} placeholder="Optional"
                    data-testid="feature-page-input" className="mt-1 h-10 rounded-lg" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#0A0D14]">Attachment link</label>
                  <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Optional"
                    data-testid="feature-attachment-input" className="mt-1 h-10 rounded-lg" />
                </div>
              </div>
              <Button type="submit" disabled={busy} data-testid="feature-submit-btn"
                className="w-full rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-sm h-11">
                {busy ? "Submitting…" : "Submit Feature Request"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const RoadmapCard = ({ item }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 hover:shadow-sm transition-shadow" data-testid={`roadmap-card-${item.id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <Badge meta={STATUS_META[item.status]} />
      <CatChip cat={item.category} />
    </div>
    <h3 className="mt-2.5 font-heading text-sm font-extrabold text-[#0A0D14]">{item.title}</h3>
    <p className="mt-1 text-xs text-[#525866] leading-relaxed">{item.short_description}</p>
    {item.status === "released" ? (
      item.release_date && <p className="mt-2.5 text-[11px] font-bold text-emerald-600">Released {fmtDate(item.release_date)}</p>
    ) : (
      item.expected_release && <p className="mt-2.5 text-[11px] font-bold text-[#8a90a0]">Expected: {item.expected_release}</p>
    )}
  </div>
);

const ReleasedRow = ({ item }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-5" data-testid={`released-row-${item.id}`}>
    <div className="flex items-start gap-3">
      <span className="h-8 w-8 shrink-0 rounded-lg bg-emerald-50 flex items-center justify-center mt-0.5"><CheckCircle size={16} weight="duotone" className="text-emerald-500" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading text-sm font-extrabold text-[#0A0D14]">{item.title}</h3>
          <CatChip cat={item.category} />
          {item.release_date && <span className="text-[11px] font-bold text-emerald-600">Released {fmtDate(item.release_date)}</span>}
        </div>
        <p className="mt-1 text-xs text-[#525866] leading-relaxed">{item.short_description}</p>
        {item.release_notes && <p className="mt-1.5 text-[11px] text-[#8a90a0] leading-relaxed">{item.release_notes}</p>}
        {item.learn_more_url && (
          <a href={item.learn_more_url} target="_blank" rel="noreferrer" data-testid={`released-learn-more-${item.id}`}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-[#0052FF] hover:underline">
            Learn more <ArrowSquareOut size={11} />
          </a>
        )}
      </div>
    </div>
  </div>
);

const RequestDetail = ({ req, onClose }) => (
  <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-lg" data-testid="request-detail-dialog">
      {req && (
        <>
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-extrabold flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-0.5 text-[#525866]">{req.reference_number}</span>
              {req.title}
            </DialogTitle>
            <DialogDescription className="text-xs flex items-center gap-2">
              {req.type === "BUG" ? "Bug report" : "Feature request"} · Submitted {fmtDate(req.created_at)}
              <Badge meta={REQ_STATUS_META[req.status]} />
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-[#FAFAFB] border border-[#E5E7EB] rounded-lg p-3 text-[#525866] whitespace-pre-wrap text-xs leading-relaxed">{req.description}</div>
            {req.business_value && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#8a90a0]">Why it's useful</div>
                <p className="mt-1 text-xs text-[#525866] whitespace-pre-wrap">{req.business_value}</p>
              </div>
            )}
            {req.page_url && (
              <a href={req.page_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#0052FF] hover:underline">
                Related page <ArrowSquareOut size={12} />
              </a>
            )}
            {req.attachment_link && (
              <a href={req.attachment_link} target="_blank" rel="noreferrer" data-testid="request-detail-attachment"
                className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-[#0052FF] hover:underline">
                Attachment <ArrowSquareOut size={12} />
              </a>
            )}
            {req.client_response && (
              <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-lg p-3" data-testid="request-detail-response">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#FF6B35]">Response from 5ads.ai</div>
                <p className="mt-1 text-xs text-[#7a3315] whitespace-pre-wrap leading-relaxed">{req.client_response}</p>
              </div>
            )}
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const MyRequests = ({ requests, onOpenBug, onOpenFeature }) => {
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const rows = (requests || []).filter((r) => filter === "all" || (filter === "bugs" ? r.type === "BUG" : r.type === "FEATURE_REQUEST"));
  return (
    <section data-testid="my-requests-section">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-heading text-base font-extrabold text-[#0A0D14]">My Requests</h2>
        <div className="flex gap-1.5">
          {[["all", "All"], ["bugs", "Bugs"], ["features", "Feature Requests"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`my-requests-filter-${k}`}
              className={`h-7 px-3 rounded-full text-[11px] font-bold transition-colors ${filter === k ? "bg-[#0A0D14] text-white" : "bg-[#F3F4F6] text-[#525866] hover:bg-[#E5E7EB]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {requests === null ? (
        <p className="mt-4 text-sm text-[#8a90a0]">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="mt-4 bg-white border border-[#E5E7EB] rounded-lg p-8 text-center" data-testid="my-requests-empty">
          <p className="font-heading text-sm font-extrabold text-[#0A0D14]">{filter === "bugs" ? "Everything working smoothly?" : "Have an idea for AdPilot?"}</p>
          <p className="mt-1 text-xs text-[#8a90a0]">
            {filter === "bugs"
              ? "If you find something that doesn't work as expected, send it to our team."
              : "Tell us what would make managing and optimizing your ads easier."}
          </p>
          <button onClick={filter === "bugs" ? onOpenBug : onOpenFeature} data-testid="my-requests-empty-cta"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#0A0D14] hover:bg-[#1c2230] text-white font-bold text-xs h-9 px-5 transition-colors">
            {filter === "bugs" ? <><Bug size={13} /> Report a Bug</> : <><Lightbulb size={13} /> Suggest a Feature</>}
          </button>
        </div>
      ) : (
        <div className="mt-4 bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5E7EB] text-left text-[10px] font-bold uppercase tracking-wide text-[#8a90a0]">
                <th className="px-4 py-2.5">Reference</th><th className="px-4 py-2.5">Type</th>
                <th className="px-4 py-2.5">Title</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => setDetail(r)} data-testid={`my-request-row-${r.reference_number}`}
                  className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFB] cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-[#0A0D14]">{r.reference_number}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-bold ${r.type === "BUG" ? "text-red-500" : "text-amber-600"}`}>
                      {r.type === "BUG" ? <Bug size={12} /> : <Lightbulb size={12} />}{r.type === "BUG" ? "Bug" : "Feature"}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#0A0D14] max-w-[260px] truncate">{r.title}</td>
                  <td className="px-4 py-3"><Badge meta={REQ_STATUS_META[r.status]} /></td>
                  <td className="px-4 py-3 text-xs text-[#8a90a0]">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RequestDetail req={detail} onClose={() => setDetail(null)} />
    </section>
  );
};

export default function Roadmap() {
  const [items, setItems] = useState(null);
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("all");
  const [bugOpen, setBugOpen] = useState(false);
  const [featOpen, setFeatOpen] = useState(false);

  const loadRequests = useCallback(() => {
    api.get("/feedback/mine").then(({ data }) => setRequests(data)).catch(() => setRequests([]));
  }, []);

  useEffect(() => {
    api.get("/roadmap").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
    loadRequests();
  }, [loadRequests]);

  const all = items || [];
  const featured = all.filter((i) => i.featured && i.status !== "released");
  const released = all.filter((i) => i.status === "released").sort((a, b) => (b.release_date || "").localeCompare(a.release_date || ""));
  const comingNext = all.filter((i) => i.status !== "released" && !i.featured);
  const filtered = filter === "all" ? null : all.filter((i) => i.status === filter);

  return (
    <div className="max-w-5xl space-y-10" data-testid="roadmap-page">
      <div className="bg-[#0A0D14] rounded-xl p-7 relative overflow-hidden" data-testid="roadmap-hero">
        <RocketLaunch size={120} weight="duotone" className="absolute -right-4 -bottom-6 text-white/5" />
        <div className="flex items-center gap-2 text-[#FF6B35] text-[10px] font-bold uppercase tracking-[0.2em]">
          <RocketLaunch size={14} weight="fill" /> Product Roadmap
        </div>
        <h2 className="mt-2 font-heading text-lg font-extrabold tracking-tight text-white">See what we're building next for AdPilot.</h2>
        <p className="mt-1.5 text-xs text-[#aeb4c2] max-w-xl leading-relaxed">
          AdPilot is continuously evolving. Explore upcoming features, follow what we're currently building, and share feedback that helps us improve the platform.
        </p>
        <div className="mt-5 flex gap-3 flex-wrap">
          <button onClick={() => setBugOpen(true)} data-testid="report-bug-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white font-bold text-xs h-10 px-5 transition-colors">
            <Bug size={14} weight="duotone" /> Report a Bug
          </button>
          <button onClick={() => setFeatOpen(true)} data-testid="suggest-feature-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#FF6B35] hover:bg-[#E85A25] text-white font-bold text-xs h-10 px-5 transition-colors">
            <Lightbulb size={14} weight="duotone" /> Suggest a Feature
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="roadmap-filters">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`roadmap-filter-${k}`}
            className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${filter === k ? "bg-[#0A0D14] text-white" : "bg-white border border-[#E5E7EB] text-[#525866] hover:bg-[#F3F4F6]"}`}>
            {l}
          </button>
        ))}
      </div>

      {items === null ? (
        <p className="text-sm text-[#8a90a0]">Loading roadmap…</p>
      ) : filtered ? (
        <section data-testid="roadmap-filtered-section">
          <h2 className="font-heading text-base font-extrabold text-[#0A0D14]">{FILTERS.find(([k]) => k === filter)?.[1]}</h2>
          {filtered.length === 0 ? (
            <p className="mt-3 text-sm text-[#8a90a0]" data-testid="roadmap-filtered-empty">Nothing here right now — check back soon.</p>
          ) : filter === "released" ? (
            <div className="mt-4 space-y-3">{filtered.map((i) => <ReleasedRow key={i.id} item={i} />)}</div>
          ) : (
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{filtered.map((i) => <RoadmapCard key={i.id} item={i} />)}</div>
          )}
        </section>
      ) : (
        <>
          {featured.length > 0 && (
            <section data-testid="currently-building-section">
              <div className="flex items-center gap-2">
                <Hammer size={17} weight="duotone" className="text-[#FF6B35]" />
                <h2 className="font-heading text-base font-extrabold text-[#0A0D14]">Currently Building</h2>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {featured.map((i) => (
                  <div key={i.id} className="bg-white border-2 border-[#FF6B35]/25 rounded-lg p-5" data-testid={`featured-card-${i.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge meta={STATUS_META[i.status]} />
                      <CatChip cat={i.category} />
                    </div>
                    <h3 className="mt-2.5 font-heading text-sm font-extrabold text-[#0A0D14]">{i.title}</h3>
                    <p className="mt-1 text-xs text-[#525866] leading-relaxed">{i.short_description}</p>
                    {i.expected_release && <p className="mt-2.5 text-[11px] font-bold text-[#FF6B35]">Expected: {i.expected_release}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}
          {comingNext.length > 0 && (
            <section data-testid="coming-next-section">
              <div className="flex items-center gap-2">
                <ClockCounterClockwise size={17} weight="duotone" className="text-[#0052FF]" />
                <h2 className="font-heading text-base font-extrabold text-[#0A0D14]">Coming Next</h2>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{comingNext.map((i) => <RoadmapCard key={i.id} item={i} />)}</div>
            </section>
          )}
          {released.length > 0 && (
            <section data-testid="recently-released-section">
              <div className="flex items-center gap-2">
                <CheckCircle size={17} weight="duotone" className="text-emerald-500" />
                <h2 className="font-heading text-base font-extrabold text-[#0A0D14]">Recently Released</h2>
              </div>
              <div className="mt-4 space-y-3">{released.map((i) => <ReleasedRow key={i.id} item={i} />)}</div>
            </section>
          )}
        </>
      )}

      <MyRequests requests={requests} onOpenBug={() => setBugOpen(true)} onOpenFeature={() => setFeatOpen(true)} />

      <p className="text-[11px] text-[#8a90a0] leading-relaxed border-t border-[#E5E7EB] pt-4" data-testid="roadmap-disclaimer">
        Roadmap features and timelines are indicative and may change based on development priorities, testing, platform dependencies and user feedback.
      </p>

      <BugDialog open={bugOpen} onOpenChange={setBugOpen} onSubmitted={loadRequests} />
      <FeatureDialog open={featOpen} onOpenChange={setFeatOpen} onSubmitted={loadRequests} />
    </div>
  );
}
